import React, { useState, useEffect, useRef } from 'react';
import { Send, GitBranch, X, MessageSquare, ChevronRight, ChevronDown, Plus, Trash2, Copy, Check, Clock, RefreshCw, Pencil, Save, LogOut, User, Menu } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Auth from './components/Auth';
import { supabase } from './lib/supabase';
import './App.css';

// Use environment variable for API URL, fallback to localhost for development
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Default conversation tree structure
const createDefaultTree = () => ({
  id: 'root',
  name: 'New Conversation',
  messages: [],
  branches: []
});

// Generate a UUID (v4)
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Create a new conversation
const createNewConversation = () => ({
  id: generateUUID(),
  tree: createDefaultTree(),
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});

function ChatApp() {
  const { user, session, signOut } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [currentPath, setCurrentPath] = useState(['root']);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [error, setError] = useState(null);
  const [branchName, setBranchName] = useState('');
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, selectedText: '', messageIndex: null });
  const [quotedReply, setQuotedReply] = useState(null); // Store quoted text for input box
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Mobile menu state
  const [backendStatus, setBackendStatus] = useState('checking'); // 'online', 'offline', 'checking'
  const [thinkingEnabled, setThinkingEnabled] = useState(false); // Toggle for thinking feature
  const [tokensUsed, setTokensUsed] = useState(0); // Track total tokens used by user
  const [showAdminModal, setShowAdminModal] = useState(false); // Admin modal for invite codes
  const [generatedCode, setGeneratedCode] = useState(''); // Recently generated invite code
  const [inviteCodes, setInviteCodes] = useState([]); // List of all invite codes

  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);
  const editTextareaRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  // Load conversations from Supabase on mount
  useEffect(() => {
    loadConversations();
  }, [user]);

  // Load token usage on mount
  useEffect(() => {
    if (user) {
      fetchTokenUsage();
    }
  }, [user]);

  // Initialize current conversation after loading
  useEffect(() => {
    if (!isLoadingConversations && currentConversationId === null && conversations.length > 0) {
      setCurrentConversationId(conversations[0].id);
    }
  }, [isLoadingConversations, currentConversationId, conversations]);

  // Save conversations to Supabase whenever they change (debounced)
  useEffect(() => {
    if (isLoadingConversations) return; // Don't save during initial load

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce saves by 5 seconds to reduce database load
    saveTimeoutRef.current = setTimeout(() => {
      saveConversations();
    }, 5000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [conversations, isLoadingConversations]);

  // Check backend health status periodically
  useEffect(() => {
    const checkBackendHealth = async () => {
      try {
        const response = await fetch(`${API_URL}/health`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status === 'ok') {
            setBackendStatus('online');
          } else {
            setBackendStatus('offline');
          }
        } else {
          setBackendStatus('offline');
        }
      } catch (error) {
        setBackendStatus('offline');
      }
    };

    // Check immediately on mount
    checkBackendHealth();

    // Then check every 30 seconds
    const interval = setInterval(checkBackendHealth, 30000);

    return () => clearInterval(interval);
  }, []);

  // Load conversations from Supabase
  const loadConversations = async () => {
    if (!user) return;

    try {
      setIsLoadingConversations(true);

      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        // Map database records to app format
        const loadedConversations = data.map(record => ({
          id: record.id,
          tree: record.tree,
          createdAt: record.created_at,
          updatedAt: record.updated_at
        }));
        setConversations(loadedConversations);
      } else {
        // Create first conversation if none exist
        const newConv = createNewConversation();
        setConversations([newConv]);
        // Save the new conversation immediately
        await saveNewConversation(newConv);
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Failed to load conversations');
      // Fallback to empty conversation
      const newConv = createNewConversation();
      setConversations([newConv]);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  // Save all conversations to Supabase
  const saveConversations = async () => {
    if (!user || conversations.length === 0) return;

    try {
      // Prepare records for upsert
      const records = conversations.map(conv => ({
        id: conv.id,
        user_id: user.id,
        tree: conv.tree,
        created_at: conv.createdAt,
        updated_at: conv.updatedAt
      }));

      const { error } = await supabase
        .from('conversations')
        .upsert(records, { onConflict: 'id' });

      if (error) throw error;

    } catch (err) {
      console.error('Error saving conversations:', err);
      // Don't show error to user for background saves
    }
  };

  // Save a single new conversation to Supabase
  const saveNewConversation = async (conversation) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('conversations')
        .insert({
          id: conversation.id,
          user_id: user.id,
          tree: conversation.tree,
          created_at: conversation.createdAt,
          updated_at: conversation.updatedAt
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error saving new conversation:', err);
    }
  };

  // Fetch token usage from Supabase
  const fetchTokenUsage = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_token_usage')
        .select('total_tokens')
        .eq('user_id', user.id)
        .single();

      if (error) {
        // Table might not exist yet or no data for user
        if (error.code !== 'PGRST116') { // PGRST116 = no rows returned
          console.error('Error fetching token usage:', error);
        }
        return;
      }

      if (data) {
        setTokensUsed(data.total_tokens || 0);
      }
    } catch (err) {
      console.error('Error in fetchTokenUsage:', err);
    }
  };

  // Generate a new invite code
  const generateInviteCode = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase.rpc('generate_invite_code', {
        p_max_uses: 1,
        p_expires_days: null,
        p_notes: null
      });

      if (error) throw error;

      setGeneratedCode(data);
      await fetchInviteCodes(); // Refresh the list
    } catch (err) {
      console.error('Error generating invite code:', err);
      setError('Failed to generate invite code');
    }
  };

  // Fetch all invite codes (admin only)
  const fetchInviteCodes = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('invite_codes')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      setInviteCodes(data || []);
    } catch (err) {
      console.error('Error fetching invite codes:', err);
    }
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversations, currentConversationId, currentPath]);

  // Get current conversation
  const getCurrentConversation = () => {
    return conversations.find(c => c.id === currentConversationId);
  };

  // Get conversation tree
  const getConversationTree = () => {
    const conv = getCurrentConversation();
    return conv ? conv.tree : createDefaultTree();
  };

  // Get current branch based on currentPath
  const getCurrentBranch = () => {
    let node = getConversationTree();
    for (let i = 1; i < currentPath.length; i++) {
      node = node.branches.find(b => b.id === currentPath[i]);
      if (!node) return getConversationTree(); // Fallback to root
    }
    return node;
  };

  // Get all parent messages for context
  const getParentMessages = () => {
    const parentMessages = [];
    let node = getConversationTree();

    // Add root messages if not at root
    if (currentPath.length > 1) {
      parentMessages.push(...node.messages);
    }

    // Traverse path and collect messages
    for (let i = 1; i < currentPath.length - 1; i++) {
      node = node.branches.find(b => b.id === currentPath[i]);
      if (node) {
        parentMessages.push(...node.messages);
      }
    }

    return parentMessages;
  };

  // Get full context (all parent + current messages)
  const getFullContext = () => {
    const parentMessages = getParentMessages();
    const currentBranch = getCurrentBranch();
    return [...parentMessages, ...currentBranch.messages];
  };

  // Update tree immutably
  const updateTree = (updater) => {
    setConversations(prevConversations => {
      return prevConversations.map(conv => {
        if (conv.id !== currentConversationId) return conv;

        const updateNode = (node, path, index) => {
          if (index === path.length - 1) {
            return updater(node);
          }
          return {
            ...node,
            branches: node.branches.map(b =>
              b.id === path[index + 1]
                ? updateNode(b, path, index + 1)
                : b
            )
          };
        };

        return {
          ...conv,
          tree: updateNode(conv.tree, currentPath, 0),
          updatedAt: new Date().toISOString()
        };
      });
    });
  };

  // Send message to AI with streaming
  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString(),
      ...(quotedReply && { quotedText: quotedReply }) // Include quoted text if present
    };

    // Add user message to current branch
    updateTree(node => ({
      ...node,
      messages: [...node.messages, userMessage]
    }));

    setInput('');
    setQuotedReply(null); // Clear quoted reply after sending
    setIsLoading(true);
    setError(null);

    // Create a placeholder AI message that will be updated as we stream
    const aiMessageId = `ai-${Date.now()}`;
    const placeholderMessage = {
      role: 'assistant',
      content: '',
      thinking: '',
      timestamp: new Date().toISOString(),
      id: aiMessageId,
      isStreaming: true,
      isThinkingComplete: false
    };

    // Add placeholder message
    updateTree(node => ({
      ...node,
      messages: [...node.messages, placeholderMessage]
    }));

    try {
      // Get full context for API call
      const fullContext = [...getFullContext(), userMessage];

      // Limit context window to prevent exceeding LLM limits
      // Keep only the last 20 messages (10 exchanges) to stay within token limits
      const MAX_CONTEXT_MESSAGES = 20;
      const contextToSend = fullContext.length > MAX_CONTEXT_MESSAGES
        ? fullContext.slice(-MAX_CONTEXT_MESSAGES)
        : fullContext;

      console.log(`Sending ${contextToSend.length} messages to LLM (from ${fullContext.length} total in context)`);

      // Format messages for API - combine quotedText and content
      const formattedMessages = contextToSend.map(msg => ({
        role: msg.role,
        content: msg.quotedText
          ? `[Replying to: "${msg.quotedText}"]\n\n${msg.content}`
          : msg.content
      }));

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Prepare headers with optional authorization
      const headers = { 'Content-Type': 'application/json' };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages: formattedMessages,
          enableThinking: thinkingEnabled
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      // Read the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedThinking = '';
      let accumulatedContent = '';
      let buffer = ''; // Buffer for incomplete lines

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('Stream done - thinking:', accumulatedThinking, 'content:', accumulatedContent);
          break;
        }

        // Add chunk to buffer
        buffer += decoder.decode(value, { stream: true });

        // Split by newlines
        const lines = buffer.split('\n');

        // Keep the last incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim() === '') continue; // Skip empty lines

          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();

            if (data === '[DONE]') {
              console.log('Received [DONE] signal');
              break;
            }

            try {
              const parsed = JSON.parse(data);

              if (parsed.error) {
                throw new Error(parsed.error);
              }

              // Handle thinking stream
              if (parsed.type === 'thinking' && parsed.content) {
                console.log('Adding thinking:', parsed.content);
                accumulatedThinking += parsed.content;

                // Update the message with thinking content
                setConversations(prevConversations =>
                  prevConversations.map(conv => {
                    if (conv.id !== currentConversationId) return conv;

                    const updateMessages = (node, path, index) => {
                      if (index === path.length - 1) {
                        return {
                          ...node,
                          messages: node.messages.map(msg =>
                            msg.id === aiMessageId
                              ? { ...msg, thinking: accumulatedThinking }
                              : msg
                          )
                        };
                      }
                      return {
                        ...node,
                        branches: node.branches.map(b =>
                          b.id === path[index + 1]
                            ? updateMessages(b, path, index + 1)
                            : b
                        )
                      };
                    };

                    return {
                      ...conv,
                      tree: updateMessages(conv.tree, currentPath, 0),
                      updatedAt: new Date().toISOString()
                    };
                  })
                );
              }

              // Handle response stream
              else if (parsed.type === 'response' && parsed.content) {
                console.log('Adding response:', parsed.content);
                accumulatedContent += parsed.content;

                // Update the message with response content and mark thinking complete
                setConversations(prevConversations =>
                  prevConversations.map(conv => {
                    if (conv.id !== currentConversationId) return conv;

                    const updateMessages = (node, path, index) => {
                      if (index === path.length - 1) {
                        return {
                          ...node,
                          messages: node.messages.map(msg =>
                            msg.id === aiMessageId
                              ? { ...msg, content: accumulatedContent, isThinkingComplete: true }
                              : msg
                          )
                        };
                      }
                      return {
                        ...node,
                        branches: node.branches.map(b =>
                          b.id === path[index + 1]
                            ? updateMessages(b, path, index + 1)
                            : b
                        )
                      };
                    };

                    return {
                      ...conv,
                      tree: updateMessages(conv.tree, currentPath, 0),
                      updatedAt: new Date().toISOString()
                    };
                  })
                );
              }
            } catch (e) {
              console.error('Error parsing stream data:', e);
            }
          }
        }
      }

      // Mark streaming as complete
      setConversations(prevConversations =>
        prevConversations.map(conv => {
          if (conv.id !== currentConversationId) return conv;

          const updateMessages = (node, path, index) => {
            if (index === path.length - 1) {
              return {
                ...node,
                messages: node.messages.map(msg =>
                  msg.id === aiMessageId
                    ? { ...msg, isStreaming: false }
                    : msg
                )
              };
            }
            return {
              ...node,
              branches: node.branches.map(b =>
                b.id === path[index + 1]
                  ? updateMessages(b, path, index + 1)
                  : b
              )
            };
          };

          return {
            ...conv,
            tree: updateMessages(conv.tree, currentPath, 0)
          };
        })
      );

      // Fetch updated token usage after stream completes
      if (user) {
        fetchTokenUsage();
      }

      // Auto-generate conversation name from first user message only
      const currentConv = getCurrentConversation();
      if (currentConv && currentConv.tree.name === 'New Conversation') {
        // Find the first user message
        const firstUserMessage = getFullContext().find(msg => msg.role === 'user');
        if (firstUserMessage) {
          generateConversationName([firstUserMessage]);
        }
      }

    } catch (err) {
      if (err.name === 'AbortError') {
        // Stream was interrupted - finalize the message with accumulated content
        setConversations(prevConversations =>
          prevConversations.map(conv => {
            if (conv.id !== currentConversationId) return conv;

            const updateMessages = (node, path, index) => {
              if (index === path.length - 1) {
                return {
                  ...node,
                  messages: node.messages.map(msg =>
                    msg.id === aiMessageId
                      ? { ...msg, isStreaming: false }
                      : msg
                  )
                };
              }
              return {
                ...node,
                branches: node.branches.map(b =>
                  b.id === path[index + 1]
                    ? updateMessages(b, path, index + 1)
                    : b
                )
              };
            };

            return {
              ...conv,
              tree: updateMessages(conv.tree, currentPath, 0)
            };
          })
        );
      } else {
        // Real error - show error message and remove placeholder
        setError(err.message);
        console.error('Error sending message:', err);

        updateTree(node => ({
          ...node,
          messages: node.messages.filter(msg => msg.id !== aiMessageId)
        }));
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  // Stop generating AI response
  const stopGenerating = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  // Copy message to clipboard
  const copyMessage = async (content, messageId) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;

    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Regenerate the last AI response
  const regenerateResponse = () => {
    if (isLoading) return;

    const currentBranch = getCurrentBranch();
    const messages = currentBranch.messages;

    // Find the last user message
    let lastUserMessageIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        lastUserMessageIndex = i;
        break;
      }
    }

    if (lastUserMessageIndex === -1) return;

    // Remove all messages after the last user message
    updateTree(node => ({
      ...node,
      messages: node.messages.slice(0, lastUserMessageIndex + 1)
    }));

    // Re-send the message
    const userMessage = messages[lastUserMessageIndex];
    setInput(userMessage.content);

    // Use setTimeout to ensure state updates properly
    setTimeout(() => {
      sendMessage();
    }, 100);
  };

  // Start editing a message
  const startEditingMessage = (messageId, content) => {
    if (isLoading) return;
    setEditingMessageId(messageId);
    setEditContent(content);
    // Focus the textarea after it's rendered
    setTimeout(() => {
      if (editTextareaRef.current) {
        editTextareaRef.current.focus();
        editTextareaRef.current.setSelectionRange(content.length, content.length);
      }
    }, 0);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditContent('');
  };

  // Save edited message and regenerate response
  const saveEditedMessage = (messageIndex) => {
    if (!editContent.trim() || isLoading) return;

    // Remove the edited message and all messages after it
    updateTree(node => ({
      ...node,
      messages: node.messages.slice(0, messageIndex)
    }));

    // Clear editing state
    setEditingMessageId(null);
    setEditContent('');

    // Set input to the edited content and send (will add message and regenerate)
    setInput(editContent);

    // Use setTimeout to ensure state updates properly
    setTimeout(() => {
      sendMessage();
    }, 100);
  };

  // Generate conversation name using AI
  const generateConversationName = async (messages) => {
    try {
      if (!messages || messages.length === 0) return;

      const response = await fetch(`${API_URL}/api/generate-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages })
      });

      if (!response.ok) {
        console.error('Failed to generate summary');
        return;
      }

      const data = await response.json();

      setConversations(prevConversations =>
        prevConversations.map(conv =>
          conv.id === currentConversationId
            ? { ...conv, tree: { ...conv.tree, name: data.summary } }
            : conv
        )
      );
    } catch (err) {
      console.error('Error generating conversation name:', err);
    }
  };

  // Create new branch
  const createBranch = () => {
    if (!branchName.trim()) return;

    const newBranchId = `branch-${Date.now()}`;
    const newBranch = {
      id: newBranchId,
      name: branchName.trim(),
      messages: [],
      branches: []
    };

    updateTree(node => ({
      ...node,
      branches: [...node.branches, newBranch]
    }));

    // Navigate to new branch
    setCurrentPath([...currentPath, newBranchId]);
    setBranchName('');
    setShowBranchModal(false);
    setIsMobileMenuOpen(false); // Close mobile menu after creating branch
  };

  // Navigate to specific branch
  const navigateToBranch = (path) => {
    setCurrentPath(path);
    setIsMobileMenuOpen(false); // Close mobile menu when navigating
  };

  // Close current branch (go to parent)
  const closeBranch = () => {
    if (currentPath.length > 1) {
      setCurrentPath(currentPath.slice(0, -1));
    }
  };

  // Handle branch button click
  const handleBranchClick = () => {
    setShowBranchModal(true);
  };

  // Handle text selection and context menu
  const handleTextSelection = (e, messageIndex) => {
    e.preventDefault();
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();

    if (selectedText) {
      setContextMenu({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        selectedText: selectedText,
        messageIndex: messageIndex
      });
    }
  };

  // Create thread from selected text
  const handleCreateThreadFromSelection = () => {
    if (!contextMenu.selectedText) return;

    // Maximum branch depth limit (root + 2 levels)
    const MAX_BRANCH_DEPTH = 3; // ['root', 'branch1', 'branch2']

    // Check if we've reached the maximum branch depth
    if (currentPath.length >= MAX_BRANCH_DEPTH) {
      setError('Maximum branch depth (2 levels) reached. Cannot create more nested branches.');
      setContextMenu({ visible: false, x: 0, y: 0, selectedText: '', messageIndex: null });
      return;
    }

    const newBranchId = `branch-${Date.now()}`;
    const newBranch = {
      id: newBranchId,
      name: contextMenu.selectedText.substring(0, 50) + (contextMenu.selectedText.length > 50 ? '...' : ''),
      messages: [], // Empty messages - user will type their question
      branches: []
    };

    updateTree(node => ({
      ...node,
      branches: [...node.branches, newBranch]
    }));

    // Set quoted text in input box and navigate to new branch
    setQuotedReply(contextMenu.selectedText);
    setCurrentPath([...currentPath, newBranchId]);
    setContextMenu({ visible: false, x: 0, y: 0, selectedText: '', messageIndex: null });
    setIsMobileMenuOpen(false); // Close mobile menu when creating branch from selection
  };

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        setContextMenu({ visible: false, x: 0, y: 0, selectedText: '', messageIndex: null });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu.visible]);

  // Render tree recursively
  const TreeNode = ({ node, path, depth = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const isActive = JSON.stringify(path) === JSON.stringify(currentPath);
    const hasChildren = node.branches && node.branches.length > 0;

    return (
      <div className="tree-node">
        <div
          className={`tree-item ${isActive ? 'active' : ''}`}
          style={{ paddingLeft: `${depth * 16}px` }}
          onClick={() => navigateToBranch(path)}
        >
          {hasChildren && (
            <button
              className="expand-button"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          )}
          {!hasChildren && <span className="expand-placeholder" />}
          <MessageSquare size={16} />
          <span className="tree-label">
            {node.name} ({node.messages.length})
          </span>
        </div>
        {isExpanded && hasChildren && (
          <div className="tree-children">
            {node.branches.map(branch => (
              <TreeNode
                key={branch.id}
                node={branch}
                path={[...path, branch.id]}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Create new conversation
  const createNewConv = async () => {
    const newConv = createNewConversation();
    setConversations(prev => [...prev, newConv]);
    setCurrentConversationId(newConv.id);
    setCurrentPath(['root']);

    // Save immediately to Supabase
    await saveNewConversation(newConv);
  };

  // Delete conversation
  const deleteConversation = async (convId) => {
    if (conversations.length === 1) return; // Don't delete last conversation

    try {
      // Delete from Supabase
      const { error } = await supabase
        .from('conversations')
        .delete()
        .eq('id', convId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state
      setConversations(prev => prev.filter(c => c.id !== convId));

      if (convId === currentConversationId) {
        const remaining = conversations.filter(c => c.id !== convId);
        if (remaining.length > 0) {
          setCurrentConversationId(remaining[0].id);
          setCurrentPath(['root']);
        }
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError('Failed to delete conversation');
    }
  };

  // Switch to a different conversation
  const switchConversation = (convId) => {
    setCurrentConversationId(convId);
    setCurrentPath(['root']);
    setIsMobileMenuOpen(false); // Close mobile menu when switching conversations
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when clicking overlay
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Render breadcrumb navigation
  const Breadcrumb = () => {
    const getBreadcrumbPath = () => {
      const paths = [];
      let node = getConversationTree();
      paths.push({ name: node.name, path: ['root'] });

      for (let i = 1; i < currentPath.length; i++) {
        node = node.branches.find(b => b.id === currentPath[i]);
        if (node) {
          paths.push({ name: node.name, path: currentPath.slice(0, i + 1) });
        }
      }

      return paths;
    };

    const paths = getBreadcrumbPath();

    return (
      <div className="breadcrumb">
        {paths.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && <ChevronRight size={16} className="breadcrumb-separator" />}
            <button
              className="breadcrumb-item"
              onClick={() => navigateToBranch(item.path)}
            >
              {item.name}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const currentBranch = getCurrentBranch();
  const parentMessages = getParentMessages();

  // Thinking Block component for displaying AI reasoning
  const ThinkingBlock = ({ thinking, isComplete }) => {
    const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default

    if (!thinking) return null;

    return (
      <div className="thinking-block">
        <button
          className="thinking-header"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
        >
          <div className="thinking-header-left">
            {!isComplete && <div className="thinking-pulse-loader"></div>}
            <svg className="thinking-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span className="thinking-label">Thinking process</span>
            {!isComplete && <span className="thinking-badge">Streaming...</span>}
          </div>
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        {isExpanded && (
          <div className={`thinking-content ${!isComplete ? 'streaming' : ''}`}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  if (inline) {
                    return <code className="inline-code" {...props}>{children}</code>;
                  }
                  return <code {...props}>{children}</code>;
                }
              }}
            >
              {thinking || 'Thinking...'}
            </ReactMarkdown>
          </div>
        )}
      </div>
    );
  };

  // Code block component with copy button
  const CodeBlock = ({ language, value }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy code:', err);
      }
    };

    return (
      <div className="code-block-wrapper">
        <div className="code-block-header">
          <span className="code-block-language">{language || 'text'}</span>
          <button className="code-copy-button" onClick={handleCopy} title="Copy code">
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={language || 'text'}
          PreTag="div"
          customStyle={{
            margin: 0,
            borderRadius: '0 0 6px 6px',
            fontSize: '14px',
          }}
        >
          {value}
        </SyntaxHighlighter>
      </div>
    );
  };

  // Custom components for ReactMarkdown with syntax highlighting
  const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const value = String(children).replace(/\n$/, '');

      return !inline && match ? (
        <CodeBlock language={match[1]} value={value} />
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  };

  // Conversation item with branch dropdown
  const ConversationItem = ({ conv }) => {
    const [isExpanded, setIsExpanded] = useState(conv.id === currentConversationId);
    const hasBranches = conv.tree.branches && conv.tree.branches.length > 0;

    return (
      <div className="conversation-wrapper">
        <div
          className={`conversation-item ${conv.id === currentConversationId ? 'active' : ''}`}
          onClick={() => switchConversation(conv.id)}
        >
          <div className="conversation-info">
            {hasBranches && (
              <button
                className="expand-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
              >
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            )}
            {!hasBranches && <span className="expand-placeholder-small" />}
            <MessageSquare size={16} />
            <span className="conversation-name">{conv.tree.name}</span>
          </div>
          {conversations.length > 1 && (
            <button
              className="delete-conversation-button"
              onClick={(e) => {
                e.stopPropagation();
                deleteConversation(conv.id);
              }}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
        {isExpanded && hasBranches && (
          <div className="branch-tree-nested">
            {conv.tree.branches.map(branch => (
              <TreeNode
                key={branch.id}
                node={branch}
                path={['root', branch.id]}
                depth={1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Show loading state while conversations are loading
  if (isLoadingConversations) {
    return (
      <div className="app">
        {/* Sidebar skeleton */}
        <div className="sidebar">
          <div className="sidebar-header">
            <div className="sidebar-header-top">
              <div className="app-branding">
                <GitBranch size={24} strokeWidth={2} className="app-logo branch-logo" />
                <h2>Aritra</h2>
              </div>
            </div>
          </div>
          <div className="conversations-list"></div>

          {/* Token Consumption Stats */}
          <div className="token-stats-container">
            <div className="token-stats-row">
              <span className="stats-label">Tokens Used:</span>
              <span className="stats-value">{tokensUsed.toLocaleString()}</span>
            </div>
            <div className="token-stats-row">
              <span className="stats-label">Power:</span>
              <span className="stats-value">~{(tokensUsed * 0.0005).toFixed(2)} Wh</span>
            </div>
          </div>

          {/* Admin Button */}
          {user && (
            <button
              className="admin-button"
              onClick={() => {
                setShowAdminModal(true);
                fetchInviteCodes();
              }}
              title="Generate Invite Codes"
            >
              Generate Invite Code
            </button>
          )}

          {user && (
            <div className="user-profile-bottom">
              {user.user_metadata?.avatar_url ? (
                <img
                  src={user.user_metadata.avatar_url}
                  alt={user.user_metadata?.full_name || user.email}
                  className="user-avatar"
                />
              ) : (
                <div className="user-avatar-placeholder">
                  <User size={16} />
                </div>
              )}
              <div className="user-info">
                <span className="user-name">{user.user_metadata?.full_name || user.email?.split('@')[0]}</span>
                <span className="user-email">{user.email}</span>
              </div>
            </div>
          )}
        </div>

        {/* Loading content area */}
        <div className="main-area">
          <div className="loading-state">
            <RefreshCw size={32} className="loading-spinner" />
            <p className="loading-text">Loading conversations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Beta Banner */}
      <div className="beta-banner">
        <span className="beta-badge">BETA</span>
        <span className="beta-message">You're using the beta version of Aritra</span>
      </div>

      <div className="app-content">
      {/* Mobile Menu Button */}
      <button
        className="mobile-menu-button"
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
      >
        <Menu size={20} />
      </button>

      {/* Mobile Overlay */}
      <div
        className={`mobile-nav-overlay ${isMobileMenuOpen ? 'visible' : ''}`}
        onClick={closeMobileMenu}
      />

      {/* Tree Sidebar */}
      <div className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-header-top">
            <div className="app-branding">
              <GitBranch size={24} strokeWidth={2} className="app-logo branch-logo" />
              <h2>Aritra</h2>
            </div>
            <button className="new-conversation-button" onClick={createNewConv}>
              <Plus size={16} />
              New
            </button>
          </div>
        </div>

        {/* Conversation List with Nested Branches */}
        <div className="conversations-list">
          {conversations.map(conv => (
            <ConversationItem key={conv.id} conv={conv} />
          ))}
        </div>

        {/* Token Consumption Stats */}
        <div className="token-stats-container">
          <div className="token-stats-row">
            <span className="stats-label">Tokens Used:</span>
            <span className="stats-value">{tokensUsed.toLocaleString()}</span>
          </div>
          <div className="token-stats-row">
            <span className="stats-label">Power:</span>
            <span className="stats-value">~{(tokensUsed * 0.0005).toFixed(2)} Wh</span>
          </div>
        </div>

        {/* Backend Status Indicator */}
        <div className="backend-status-container">
          <div className="backend-status-indicator">
            <div className={`status-dot ${backendStatus}`}></div>
            <span className="status-text">
              {backendStatus === 'online' ? 'Aritra Online - Fire away!' :
               backendStatus === 'offline' ? 'Currently Offline - Rebooting the imagination…' :
               'Checking...'}
            </span>
          </div>
          {backendStatus === 'offline' && (
            <span className="status-subtitle">Try again later (Beta)</span>
          )}
        </div>

        {/* User Profile at Bottom */}
        {user && (
          <div className="user-profile-bottom">
            {user.user_metadata?.avatar_url ? (
              <img
                src={user.user_metadata.avatar_url}
                alt={user.user_metadata?.full_name || user.email}
                className="user-avatar"
              />
            ) : (
              <div className="user-avatar-placeholder">
                <User size={16} />
              </div>
            )}
            <div className="user-info">
              <span className="user-name">{user.user_metadata?.full_name || user.email?.split('@')[0]}</span>
              <span className="user-email">{user.email}</span>
            </div>
            <button className="logout-button" onClick={signOut} title="Sign out">
              <LogOut size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Main Chat Area */}
      <div className="main-area">
        {/* Show landing page if no messages */}
        {currentBranch.messages.length === 0 && currentPath.length === 1 ? (
          <div className="landing-page">
            <div className="landing-content">
              <div className="landing-header">
                <div className="landing-logo">
                  <GitBranch size={32} strokeWidth={2} className="branch-logo" />
                </div>
                <h1 className="landing-title">What can Aritra help you with?</h1>
              </div>

              {/* Input Box in Center */}
              <div className="landing-input-container">
                {quotedReply && (
                  <div className="input-quoted-reply">
                    <div className="quoted-text-input">
                      {quotedReply}
                    </div>
                    <button
                      className="clear-quote-button"
                      onClick={() => setQuotedReply(null)}
                      title="Remove quoted text"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                <div className="input-row">
                  <textarea
                    className="input-box"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder={quotedReply ? "Type your question about this..." : "Type your message... (Enter to send, Shift+Enter for new line)"}
                    disabled={isLoading}
                  />
                  {isLoading ? (
                    <button
                      className="stop-button"
                      onClick={stopGenerating}
                      title="Stop generating"
                    >
                      <X size={20} />
                    </button>
                  ) : (
                    <button
                      className="send-button"
                      onClick={sendMessage}
                      disabled={!input.trim()}
                    >
                      <Send size={20} />
                    </button>
                  )}
                </div>

                {/* Thinking Toggle below input */}
                <div className="thinking-toggle-container">
                  <input
                    type="checkbox"
                    id="thinking-toggle-landing"
                    checked={thinkingEnabled}
                    onChange={(e) => setThinkingEnabled(e.target.checked)}
                    className="thinking-toggle-checkbox"
                  />
                  <label htmlFor="thinking-toggle-landing" className="thinking-toggle-label"></label>
                  <label htmlFor="thinking-toggle-landing" className="thinking-toggle-text">Deep thinking</label>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Breadcrumb Navigation */}
            <div className="breadcrumb-container">
              <Breadcrumb />
              {currentPath.length > 1 && (
                <button className="close-branch-button" onClick={closeBranch}>
                  <X size={16} />
                  Close Branch
                </button>
              )}
            </div>

            {/* Messages */}
            <div className="messages-container">
          {/* Parent messages (dimmed) */}
          {parentMessages.length > 0 && (
            <div className="parent-messages">
              {parentMessages.map((msg, index) => (
                <div
                  key={`parent-${index}`}
                  className={`message ${msg.role}`}
                >
                  <div className="message-header">
                    {msg.timestamp && (
                      <span className="message-timestamp">
                        <Clock size={12} />
                        {formatTimestamp(msg.timestamp)}
                      </span>
                    )}
                  </div>
                  <div className="message-content">
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {msg.content}
                      </ReactMarkdown>
                    ) : (
                      msg.content
                    )}
                  </div>
                </div>
              ))}
              <div className="branch-separator">
                <GitBranch size={16} className="branch-logo" />
                <span>Branched conversation starts here</span>
              </div>
            </div>
          )}

          {/* Current branch messages */}
          {currentBranch.messages.map((msg, index) => {
            const isLastAssistantMessage =
              msg.role === 'assistant' &&
              index === currentBranch.messages.length - 1 &&
              !msg.isStreaming;
            const isEditing = editingMessageId === (msg.id || index);

            return (
              <div
                key={msg.id || index}
                className={`message ${msg.role} ${isEditing ? 'editing' : ''}`}
                onMouseEnter={(e) => {
                  if (msg.role === 'assistant' && !msg.isStreaming) {
                    e.currentTarget.querySelector('.branch-button')?.classList.add('visible');
                    e.currentTarget.querySelector('.copy-button')?.classList.add('visible');
                    if (isLastAssistantMessage) {
                      e.currentTarget.querySelector('.regenerate-button')?.classList.add('visible');
                    }
                  }
                  if (msg.role === 'user' && !isEditing) {
                    e.currentTarget.querySelector('.edit-button')?.classList.add('visible');
                  }
                }}
                onMouseLeave={(e) => {
                  if (msg.role === 'assistant') {
                    e.currentTarget.querySelector('.branch-button')?.classList.remove('visible');
                    e.currentTarget.querySelector('.copy-button')?.classList.remove('visible');
                    e.currentTarget.querySelector('.regenerate-button')?.classList.remove('visible');
                  }
                  if (msg.role === 'user' && !isEditing) {
                    e.currentTarget.querySelector('.edit-button')?.classList.remove('visible');
                  }
                }}
              >
                <div className="message-header">
                  {msg.timestamp && (
                    <span className="message-timestamp">
                      <Clock size={12} />
                      {formatTimestamp(msg.timestamp)}
                    </span>
                  )}
                </div>
                {isEditing ? (
                  <div className="message-edit-container">
                    <textarea
                      ref={editTextareaRef}
                      className="edit-textarea"
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          cancelEditing();
                        }
                      }}
                    />
                    <div className="edit-actions">
                      <button
                        className="save-edit-button"
                        onClick={() => saveEditedMessage(index)}
                        disabled={!editContent.trim()}
                      >
                        <Save size={14} />
                        <span>Save & Regenerate</span>
                      </button>
                      <button className="cancel-edit-button" onClick={cancelEditing}>
                        <X size={14} />
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {msg.role === 'user' ? (
                      <div className="user-message-container">
                        <div className={`message-content ${msg.isStreaming ? 'streaming' : ''}`}>
                          {msg.quotedText && (
                            <div className="quoted-text">
                              {msg.quotedText}
                            </div>
                          )}
                          {msg.content || (msg.quotedText ? 'Type your question about this...' : '\u00A0')}
                        </div>
                        <div className="message-actions">
                          <button
                            className="edit-button"
                            onClick={() => startEditingMessage(msg.id || index, msg.content)}
                            title="Edit message"
                          >
                            <Pencil size={14} />
                            <span>Edit</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Show thinking block for assistant messages (only if enabled) */}
                        {thinkingEnabled && msg.thinking && (
                          <ThinkingBlock
                            thinking={msg.thinking}
                            isComplete={msg.isThinkingComplete}
                          />
                        )}

                        {/* Show main response */}
                        <div
                          className={`message-content ${msg.isStreaming ? 'streaming' : ''}`}
                          onContextMenu={(e) => handleTextSelection(e, index)}
                        >
                          {msg.content ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                              {msg.content}
                            </ReactMarkdown>
                          ) : (
                            '\u00A0'
                          )}
                        </div>
                      </>
                    )}
                    {msg.role === 'assistant' && !msg.isStreaming && (
                      <div className="message-actions">
                        <button
                          className="copy-button"
                          onClick={() => copyMessage(msg.content, msg.id || index)}
                          title="Copy message"
                        >
                          {copiedMessageId === (msg.id || index) ? (
                            <>
                              <Check size={14} />
                              <span>Copied</span>
                            </>
                          ) : (
                            <>
                              <Copy size={14} />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                        {isLastAssistantMessage && (
                          <button
                            className="regenerate-button"
                            onClick={regenerateResponse}
                            title="Regenerate response"
                          >
                            <RefreshCw size={14} />
                            <span>Regenerate</span>
                          </button>
                        )}
                        <button
                          className="branch-button"
                          onClick={handleBranchClick}
                        >
                          <GitBranch size={14} className="branch-logo" />
                          <span>Branch</span>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {/* Loading indicator */}
          {isLoading && !currentBranch.messages.some(msg => msg.isStreaming) && (
            <div className="message assistant">
              <div className="message-content loading">Thinking...</div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area at Bottom - Only when there are messages */}
        <div className="input-container">
          {quotedReply && (
            <div className="input-quoted-reply">
              <div className="quoted-text-input">
                {quotedReply}
              </div>
              <button
                className="clear-quote-button"
                onClick={() => setQuotedReply(null)}
                title="Remove quoted text"
              >
                <X size={16} />
              </button>
            </div>
          )}

          <div className="input-row">
            <textarea
              className="input-box"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder={quotedReply ? "Type your question about this..." : "Type your message... (Enter to send, Shift+Enter for new line)"}
              disabled={isLoading}
            />
            {isLoading ? (
              <button
                className="stop-button"
                onClick={stopGenerating}
                title="Stop generating"
              >
                <X size={20} />
              </button>
            ) : (
              <button
                className="send-button"
                onClick={sendMessage}
                disabled={!input.trim()}
              >
                <Send size={20} />
              </button>
            )}
          </div>

          {/* Thinking Toggle below input */}
          <div className="thinking-toggle-container">
            <input
              type="checkbox"
              id="thinking-toggle-chat"
              checked={thinkingEnabled}
              onChange={(e) => setThinkingEnabled(e.target.checked)}
              className="thinking-toggle-checkbox"
            />
            <label htmlFor="thinking-toggle-chat" className="thinking-toggle-label"></label>
            <label htmlFor="thinking-toggle-chat" className="thinking-toggle-text">Deep thinking</label>
          </div>
        </div>
          </>
        )}
      </div>

      {/* Branch Modal */}
      {showBranchModal && (
        <div className="modal-overlay" onClick={() => setShowBranchModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Branch</h3>
            <input
              type="text"
              className="branch-name-input"
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  createBranch();
                }
              }}
              placeholder="Enter branch name..."
              autoFocus
            />
            <div className="modal-buttons">
              <button className="cancel-button" onClick={() => setShowBranchModal(false)}>
                Cancel
              </button>
              <button className="create-button" onClick={createBranch}>
                Create Branch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Modal for Invite Codes */}
      {showAdminModal && (
        <div className="modal-overlay" onClick={() => setShowAdminModal(false)}>
          <div className="modal admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Invite Code Generator</h3>

            <button className="create-button" onClick={generateInviteCode} style={{marginBottom: '20px', width: '100%'}}>
              Generate New Code
            </button>

            {generatedCode && (
              <div className="generated-code-display" style={{
                padding: '15px',
                background: '#1a1a1a',
                borderRadius: '8px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <p style={{margin: '0 0 10px 0', fontSize: '12px', color: '#999'}}>New Invite Code:</p>
                <p style={{margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#2196f3', fontFamily: 'monospace'}}>
                  {generatedCode}
                </p>
              </div>
            )}

            <div className="invite-codes-list" style={{maxHeight: '300px', overflowY: 'auto'}}>
              <h4 style={{margin: '0 0 10px 0', fontSize: '14px'}}>Recent Codes</h4>
              {inviteCodes.length === 0 ? (
                <p style={{color: '#666', fontSize: '12px'}}>No invite codes yet</p>
              ) : (
                inviteCodes.map(code => (
                  <div key={code.id} style={{
                    padding: '10px',
                    background: '#1a1a1a',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    fontSize: '12px'
                  }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px'}}>
                      <span style={{fontFamily: 'monospace', fontWeight: 'bold', color: '#2196f3'}}>
                        {code.code}
                      </span>
                      <span style={{color: code.current_uses >= code.max_uses ? '#f44336' : '#4caf50'}}>
                        {code.current_uses}/{code.max_uses} used
                      </span>
                    </div>
                    <div style={{color: '#666', fontSize: '10px'}}>
                      Created: {new Date(code.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modal-buttons" style={{marginTop: '20px'}}>
              <button className="cancel-button" onClick={() => {
                setShowAdminModal(false);
                setGeneratedCode('');
              }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu for Text Selection */}
      {contextMenu.visible && (
        <div
          className="context-menu"
          style={{
            position: 'fixed',
            top: `${contextMenu.y}px`,
            left: `${contextMenu.x}px`,
            zIndex: 1000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="context-menu-item"
            onClick={handleCreateThreadFromSelection}
            disabled={currentPath.length >= 3}
            title={currentPath.length >= 3 ? 'Maximum branch depth (2 levels) reached' : 'Create new thread with selection'}
          >
            <GitBranch size={16} className="branch-logo" />
            <span>Create new thread with selection</span>
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

// Main App component with auth wrapper
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

// App content that checks authentication
function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#000000',
        color: '#ffffff'
      }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Auth />;
  }

  return <ChatApp />;
}

export default App;
